export type User = {
  id: string;
  email: string;
  fullName: string;
  userType: 'EMPLOYEE' | 'CLIENT';
  role: string;
  roles?: string[];
  companyId: string | null;
  authSource?: 'LOCAL' | 'ACTIVE_DIRECTORY' | 'HYBRID';
  avatarUpdatedAt?: string | null;
};

let csrfToken = '';
let csrfRefreshPromise: Promise<string> | null = null;

export const AUTH_EXPIRED_EVENT = 'intez:auth-expired';
export const AUTH_KEEPALIVE_INTERVAL_MS = 5 * 60_000;

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

export function isUnauthorizedError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 401;
}

type SessionKeepaliveOptions = {
  refresh: () => Promise<unknown>;
  expire: () => void;
  intervalMs?: number;
  schedule?: (handler: () => void, intervalMs: number) => unknown;
  cancel?: (timer: unknown) => void;
  listen?: (name: 'auth-expired' | 'visibilitychange' | 'online' | 'pageshow', handler: () => void) => () => void;
  isVisible?: () => boolean;
};

export function startSessionKeepalive(options: SessionKeepaliveOptions): () => void {
  const schedule = options.schedule || ((handler, intervalMs) => window.setInterval(handler, intervalMs));
  const cancel = options.cancel || (timer => window.clearInterval(timer as number));
  const listen = options.listen || ((name, handler) => {
    const browserEvent = name === 'auth-expired' ? AUTH_EXPIRED_EVENT : name;
    window.addEventListener(browserEvent, handler);
    return () => window.removeEventListener(browserEvent, handler);
  });
  const isVisible = options.isVisible || (() => document.visibilityState !== 'hidden');
  let expired = false;
  const expireOnce = () => {
    if (expired) return;
    expired = true;
    options.expire();
  };
  const refresh = () => {
    void options.refresh().catch(error => {
      if (isUnauthorizedError(error)) expireOnce();
    });
  };
  const timer = schedule(refresh, options.intervalMs || AUTH_KEEPALIVE_INTERVAL_MS);
  const cleanups = [
    listen('auth-expired', expireOnce),
    listen('visibilitychange', () => { if (isVisible()) refresh(); }),
    listen('online', refresh),
    listen('pageshow', refresh),
  ];
  return () => {
    cancel(timer);
    cleanups.forEach(cleanup => cleanup());
  };
}

export function setCsrf(value: string) {
  csrfToken = value || '';
}

function isUnsafeMethod(method: string) {
  return !['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase());
}

function isPublicAuthRequest(path: string) {
  return path === '/auth/login' || path === '/auth/local-login' || path === '/auth/directory/login';
}

function messageFromBody(body: any) {
  return body?.message ?? body?.error?.message ?? 'Ошибка запроса';
}

function isCsrfError(status: number, body: any) {
  const message = String(messageFromBody(body));
  return status === 403 && /csrf|csrf-токен|токен/i.test(message);
}

async function refreshCsrfToken(keepalive = false) {
  if (csrfRefreshPromise) return csrfRefreshPromise;

  csrfRefreshPromise = fetch('/api/v1/auth/session', {
    method: 'GET',
    credentials: 'include',
    keepalive,
    headers: { Accept: 'application/json' },
  })
    .then(async response => {
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setCsrf('');
        throw new Error(messageFromBody(body));
      }
      const nextToken = String(body?.data?.csrfToken ?? '');
      setCsrf(nextToken);
      return nextToken;
    })
    .finally(() => {
      csrfRefreshPromise = null;
    });

  return csrfRefreshPromise;
}

async function requestJson<T>(path: string, options: RequestInit = {}, retryCsrf = true): Promise<T> {
  const method = String(options.method ?? 'GET').toUpperCase();
  const unsafe = isUnsafeMethod(method);

  if (unsafe && !csrfToken && !isPublicAuthRequest(path)) {
    await refreshCsrfToken(options.keepalive === true);
  }

  const headers = new Headers(options.headers);
  if (options.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (unsafe && csrfToken) headers.set('X-CSRF-Token', csrfToken);

  const response = await fetch(`/api/v1${path}`, {
    ...options,
    method,
    headers,
    credentials: 'include',
  });

  const body = await response.json().catch(() => ({}));

  const responseToken = body?.data?.csrfToken;
  if (typeof responseToken === 'string' && responseToken) setCsrf(responseToken);

  if (!response.ok && retryCsrf && unsafe && isCsrfError(response.status, body) && !isPublicAuthRequest(path)) {
    setCsrf('');
    await refreshCsrfToken(options.keepalive === true);
    return requestJson<T>(path, options, false);
  }

  if (!response.ok) {
    if (response.status === 401 && !isPublicAuthRequest(path) && typeof window !== 'undefined') {
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    }
    throw new ApiError(messageFromBody(body), response.status);
  }
  return body.data as T;
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  return requestJson<T>(path, options);
}

function filenameFromDisposition(value: string | null) {
  if (!value) return '';
  const encoded = value.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try { return decodeURIComponent(encoded.trim().replace(/^"|"$/g, '')); }
    catch { return ''; }
  }
  return value.match(/filename\s*=\s*"([^"]+)"/i)?.[1]
    || value.match(/filename\s*=\s*([^;\s]+)/i)?.[1]
    || '';
}

export async function downloadApiFile(path: string): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(`/api/v1${path}`, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/octet-stream, application/zip' },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    if (response.status === 401 && typeof window !== 'undefined') {
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    }
    throw new ApiError(messageFromBody(body), response.status);
  }
  const contentType = String(response.headers.get('Content-Type') || '').toLowerCase();
  if (!contentType.includes('application/zip') && !contentType.includes('application/octet-stream')) {
    const body = contentType.includes('application/json')
      ? await response.json().catch(() => ({}))
      : {};
    throw new ApiError(messageFromBody(body) || 'Сервер вернул неожиданный формат файла', response.status);
  }
  const blob = await response.blob();
  const filename = filenameFromDisposition(response.headers.get('Content-Disposition')) || 'intez-download.bin';
  return { blob, filename: filename.replace(/[\\/\r\n]/g, '_') };
}

export async function uploadBinary<T>(path: string, body: Blob, retryCsrf = true): Promise<T> {
  if (!csrfToken) await refreshCsrfToken();

  const response = await fetch(`/api/v1${path}`, {
    method: 'PUT',
    body,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/octet-stream',
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok && retryCsrf && isCsrfError(response.status, data)) {
    setCsrf('');
    await refreshCsrfToken();
    return uploadBinary<T>(path, body, false);
  }

  if (!response.ok) throw new Error(messageFromBody(data) ?? 'Ошибка загрузки');
  return data.data as T;
}

export async function uploadImage<T>(path: string, file: File | Blob, retryCsrf = true): Promise<T> {
  if (!csrfToken) await refreshCsrfToken();

  const response = await fetch(`/api/v1${path}`, {
    method: 'PUT',
    body: file,
    credentials: 'include',
    headers: {
      'Content-Type': file.type,
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok && retryCsrf && isCsrfError(response.status, data)) {
    setCsrf('');
    await refreshCsrfToken();
    return uploadImage<T>(path, file, false);
  }

  if (!response.ok) {
    if (response.status === 401 && typeof window !== 'undefined') {
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    }
    throw new ApiError(messageFromBody(data), response.status);
  }
  return data.data as T;
}

export function formatBytes(n: number) {
  if (!n) return '0 Б';
  const units = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];
  const index = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  return `${(n / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
}

export async function uploadFormData<T>(path: string, form: FormData, retryCsrf = true): Promise<T> {
  if (!csrfToken) await refreshCsrfToken();

  const response = await fetch(`/api/v1${path}`, {
    method: 'POST',
    body: form,
    credentials: 'include',
    headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : undefined,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok && retryCsrf && isCsrfError(response.status, data)) {
    setCsrf('');
    await refreshCsrfToken();
    return uploadFormData<T>(path, form, false);
  }

  if (!response.ok) throw new Error(messageFromBody(data) ?? 'Ошибка отправки файлов');
  return data.data as T;
}
